import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MapPin, Upload, Rocket, GraduationCap, Users2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import useSEO from "../hooks/useSEO";
import { FadeUp, SectionTag, EASE } from "../components/motion";

const WHY_JOIN = [
  { icon: Rocket, title: "Real-World Engineering", desc: "Work on live surveillance grids, data centres and enterprise software — not slide decks." },
  { icon: GraduationCap, title: "Learn Across Domains", desc: "Software, hardware, networks and security — multidisciplinary growth by design." },
  { icon: Users2, title: "Small Teams, Big Ownership", desc: "Direct client exposure and end-to-end responsibility from day one." },
  { icon: TrendingUp, title: "Grow With SGS", desc: "Technical, project-management, sales and internship tracks as we scale." },
];

const inputCls = "w-full px-4 py-3 rounded-lg bg-[#F1F5F9] border border-slate-300 text-sm text-slate-700 placeholder:text-slate-600 focus:outline-none focus:border-blue-600/60 transition-colors";

function ApplyModal({ job, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience: "", message: "" });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) {
      toast.error("Please attach your resume (PDF, DOC or DOCX).");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("position", job.title);
      fd.append("resume", resume);
      await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted. Our HR team will be in touch.");
      onSuccess();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="apply-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.35, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-panel border border-slate-200 rounded-2xl p-7 max-h-[90vh] overflow-y-auto"
        data-testid="apply-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-600">Apply Now</span>
            <h3 className="mt-1 font-heading text-xl font-bold text-slate-900">{job.title}</h3>
          </div>
          <button onClick={onClose} data-testid="apply-modal-close" className="p-2 text-slate-500 hover:text-slate-700"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="apply-name-input" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="apply-email-input" />
            <input required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="apply-phone-input" />
          </div>
          <input placeholder="Experience (e.g. 3 years)" value={form.experience} onChange={set("experience")} className={inputCls} data-testid="apply-experience-input" />
          <textarea rows={3} placeholder="Message (optional)" value={form.message} onChange={set("message")} className={inputCls} data-testid="apply-message-input" />
          <label className="flex items-center justify-center gap-2.5 px-4 py-5 rounded-lg border border-dashed border-slate-300 hover:border-blue-600/50 cursor-pointer transition-colors" data-testid="apply-resume-upload">
            <Upload size={16} className="text-blue-600" />
            <span className="text-sm text-slate-600">{resume ? resume.name : "Upload resume — PDF / DOC / DOCX (max 10 MB)"}</span>
            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files[0] || null)} />
          </label>
          <button type="submit" disabled={submitting} data-testid="apply-submit-button"
            className="w-full py-3.5 rounded-full bg-blue-600 text-[#FFFFFF] font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50">
            {submitting ? "Submitting…" : "Submit Application"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function Careers() {
  useSEO(
    "Careers at SATI Global Systems (SGSNet) — IT, CCTV & Networking Jobs in Patna",
    "Join SATI Global Systems (SGSNet), Patna. Open roles for network engineers, CCTV surveillance engineers, chip-level technicians, software developers, project managers and sales consultants."
  );
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadJobs = () => {
    api.get("/jobs")
      .then((res) => setJobs(res.data))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  };
  useEffect(loadJobs, []);

  return (
    <main data-testid="careers-page" className="pt-16 sm:pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp><SectionTag>Careers</SectionTag></FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.08] max-w-3xl">
            Build the future <span className="text-blue-600">with us.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Join a team that designs, builds and maintains the technology systems entire
            institutions depend on.
          </p>
        </FadeUp>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY_JOIN.map((w, i) => (
            <FadeUp key={w.title} delay={i * 0.08}>
              <div className="bg-panel border border-slate-200 rounded-xl p-6 h-full hover:border-blue-600/40 transition-colors" data-testid={`why-join-${i}`}>
                <w.icon size={22} className="text-blue-600" strokeWidth={1.6} />
                <h3 className="mt-4 font-heading font-semibold text-slate-900 text-[15px]">{w.title}</h3>
                <p className="mt-2 text-[13px] text-slate-500 leading-relaxed">{w.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <div className="mt-24">
          <FadeUp>
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Open positions</h2>
              <span className="font-mono text-xs text-slate-500">{jobs.length} roles</span>
            </div>
          </FadeUp>
          {loading ? (
            <div className="mt-8 space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-xl bg-panel border border-slate-200 animate-pulse" />)}</div>
          ) : jobs.length === 0 ? (
            <p className="mt-8 text-slate-500 text-sm" data-testid="no-jobs-message">No open positions right now — send us your resume at mysgsinfo@gmail.com and we'll keep it on file.</p>
          ) : (
            <div className="mt-8 space-y-3">
              {jobs.map((job, i) => (
                <FadeUp key={job.id} delay={i * 0.05}>
                  <div className="group flex flex-col sm:flex-row sm:items-center gap-4 p-6 rounded-xl bg-panel border border-slate-200 hover:border-blue-600/40 transition-colors"
                    data-testid={`job-card-${i}`}>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">{job.title}</h3>
                      <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><MapPin size={12} className="text-blue-600" />{job.location}</span>
                        <span>{job.type}</span>
                        {job.experience && <span>{job.experience}</span>}
                        {job.department && <span className="font-mono uppercase tracking-wider text-blue-600/70">{job.department}</span>}
                      </p>
                      {job.description && <p className="mt-2.5 text-sm text-slate-500 leading-relaxed">{job.description}</p>}
                    </div>
                    <button onClick={() => setSelected(job)} data-testid={`job-apply-button-${i}`}
                      className="shrink-0 px-6 py-3 rounded-full border border-blue-600/50 text-blue-600 text-sm font-semibold hover:bg-blue-600 hover:text-[#FFFFFF] transition-colors">
                      Apply Now
                    </button>
                  </div>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {selected && <ApplyModal job={selected} onClose={() => setSelected(null)} onSuccess={() => setSelected(null)} />}
      </AnimatePresence>
    </main>
  );
}
